/**
 * ImapMailboxSection — Iter Feb 2026 R47 · Phase 2.
 * Per-workspace IMAP mailbox config (BigRock, HostGator, cPanel, Zoho, Gmail).
 * Enables background polling of the inbox so replies to sends made via
 * the Manual Mail Composer are captured and threaded back to the entity.
 *
 * Rendered inside Settings → Integrations (below the SMTP outbound card).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Save, Mailbox, RefreshCw, ShieldCheck } from "lucide-react";

export default function ImapMailboxSection() {
  const { activeWorkspaceId } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({
    enabled: false,
    imap_host: "",
    imap_port: 993,
    imap_use_ssl: true,
    imap_username: "",
    imap_password: "",
    imap_folder: "INBOX",
  });
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [polling, setPolling] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const load = () => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/settings/imap-mailbox`)
      .then(r => {
        setCfg(r.data);
        setForm(f => ({
          ...f,
          enabled: !!r.data.enabled,
          imap_host: r.data.imap_host || "",
          imap_port: r.data.imap_port || 993,
          imap_use_ssl: r.data.imap_use_ssl !== false,
          imap_folder: r.data.imap_folder || "INBOX",
        }));
      })
      .catch(() => setCfg({}));
  };
  useEffect(load, [activeWorkspaceId]); // eslint-disable-line

  const save = async () => {
    if (form.enabled && !form.imap_host.trim()) { toast.error("IMAP host is required"); return; }
    if (form.enabled && !form.imap_username.trim() && !cfg?.has_username) { toast.error("IMAP username is required on first save"); return; }
    if (form.enabled && !form.imap_password.trim() && !cfg?.has_password) { toast.error("IMAP password is required on first save"); return; }
    setBusy(true);
    try {
      await api.put(`/workspaces/${activeWorkspaceId}/settings/imap-mailbox`, {
        enabled: form.enabled,
        imap_host: form.imap_host.trim() || null,
        imap_port: parseInt(form.imap_port, 10) || 993,
        imap_use_ssl: form.imap_use_ssl,
        imap_username: form.imap_username.trim() || null,
        imap_password: form.imap_password.trim() || null,
        imap_folder: form.imap_folder.trim() || "INBOX",
      });
      toast.success("IMAP mailbox saved (credentials encrypted at rest)");
      setForm(f => ({ ...f, imap_password: "" }));
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  const runTest = async () => {
    setTesting(true); setLastResult(null);
    try {
      const r = await api.post(`/workspaces/${activeWorkspaceId}/settings/imap-mailbox/test`);
      setLastResult({ ok: r.data.ok, message: r.data.message });
      if (r.data.ok) toast.success("IMAP connection OK");
      else toast.error(r.data.message);
    } catch (e) { toast.error(apiErrorMessage(e, "Test failed")); }
    finally { setTesting(false); }
  };

  const runPoll = async () => {
    setPolling(true); setLastResult(null);
    try {
      const r = await api.post(`/workspaces/${activeWorkspaceId}/settings/imap-mailbox/poll`);
      setLastResult(r.data);
      toast.success(`Scanned ${r.data.scanned || 0} · captured ${r.data.recorded || 0} replies`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Poll failed")); }
    finally { setPolling(false); }
  };

  if (!cfg) return <div className="text-sm text-muted-foreground py-6">Loading IMAP mailbox…</div>;

  return (
    <div className="border border-border p-6 space-y-4" data-testid="imap-mailbox-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 border border-border flex items-center justify-center bg-blue-50">
          <Mailbox className="w-5 h-5 text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm">IMAP inbox — reply capture</div>
          <p className="text-xs text-muted-foreground">
            Connect your BigRock / HostGator / Zoho / Gmail mailbox via IMAP so replies to emails you send via the Manual Composer get pulled back into Acquilix and threaded to the original Lead / Prospect / Client. Polled every 90s.
          </p>
        </div>
        <label className="flex items-center gap-1 text-xs cursor-pointer whitespace-nowrap">
          <input type="checkbox" checked={form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked })} data-testid="imap-enabled" />
          <span className="font-semibold">Enabled</span>
        </label>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div className="col-span-2">
          <Label className="text-[10px] text-muted-foreground">IMAP host</Label>
          <Input value={form.imap_host} onChange={e => setForm({ ...form, imap_host: e.target.value })}
                 placeholder="imap.hostgator.com · imap.bigrock.in · imap.zoho.in · imap.gmail.com"
                 className="h-9 rounded-none font-mono text-xs" data-testid="imap-host" />
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground">Port</Label>
          <Input type="number" value={form.imap_port}
                 onChange={e => setForm({ ...form, imap_port: parseInt(e.target.value, 10) || 993 })}
                 placeholder="993" className="h-9 rounded-none font-mono text-xs" data-testid="imap-port" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] text-muted-foreground">Username (usually full email)</Label>
          <Input value={form.imap_username}
                 onChange={e => setForm({ ...form, imap_username: e.target.value })}
                 placeholder={cfg.has_username ? "•••••• (leave blank to keep)" : "admin@yourdomain.com"}
                 className="h-9 rounded-none font-mono text-xs" data-testid="imap-username" />
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground">Password</Label>
          <Input type="password" value={form.imap_password}
                 onChange={e => setForm({ ...form, imap_password: e.target.value })}
                 placeholder={cfg.has_password ? "•••••• (leave blank to keep)" : "mailbox password"}
                 className="h-9 rounded-none font-mono text-xs" data-testid="imap-password" />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 items-end">
        <div>
          <Label className="text-[10px] text-muted-foreground">Folder</Label>
          <Input value={form.imap_folder}
                 onChange={e => setForm({ ...form, imap_folder: e.target.value })}
                 placeholder="INBOX"
                 className="h-9 rounded-none font-mono text-xs" data-testid="imap-folder" />
        </div>
        <label className="flex items-center gap-2 text-xs h-9 cursor-pointer">
          <input type="checkbox" checked={form.imap_use_ssl}
                 onChange={e => setForm({ ...form, imap_use_ssl: e.target.checked })}
                 data-testid="imap-use-ssl" />
          <ShieldCheck className="w-3.5 h-3.5" /> SSL (port 993)
        </label>
        <div className="text-[10px] text-muted-foreground">
          {cfg.last_polled_at ? `Last poll: ${new Date(cfg.last_polled_at).toLocaleString()} · UID ${cfg.last_uid_seen}` : "Not polled yet"}
        </div>
      </div>

      <div className="flex items-center gap-2 pt-2 border-t border-border">
        <Button onClick={save} disabled={busy} className="rounded-none" data-testid="imap-save">
          {busy ? "Saving…" : <><Save className="w-4 h-4 mr-1.5" /> Save</>}
        </Button>
        <Button variant="outline" onClick={runTest} disabled={testing} className="rounded-none" data-testid="imap-test">
          {testing ? "Testing…" : "Test connection"}
        </Button>
        <Button variant="outline" onClick={runPoll} disabled={polling} className="rounded-none" data-testid="imap-poll">
          {polling ? "Polling…" : <><RefreshCw className="w-4 h-4 mr-1.5" /> Poll now</>}
        </Button>
      </div>

      {lastResult && (
        <div className={`border-2 p-3 rounded-none text-xs ${lastResult.ok === false ? "border-rose-500/60 bg-rose-50" : "border-emerald-500/60 bg-emerald-50"}`}>
          <div className="font-semibold">Last result</div>
          <pre className="whitespace-pre-wrap text-[11px] font-mono">{JSON.stringify(lastResult, null, 2)}</pre>
        </div>
      )}

      <details className="text-[10px] text-muted-foreground border border-dashed border-blue-500/40 p-2">
        <summary className="cursor-pointer text-blue-700 font-semibold">Where do I find these? (BigRock · HostGator · Zoho · Gmail)</summary>
        <div className="pt-1 space-y-1 leading-relaxed">
          <p><b>BigRock / HostGator</b>: cPanel → <i>Email Accounts</i> → your mailbox → <i>Connect Devices</i> → <i>Manual settings</i>. IMAP host: <code>mail.yourdomain.com</code> or the shown value · port <code>993</code> · SSL ON.</p>
          <p><b>Zoho</b>: <code>imap.zoho.in</code> · port <code>993</code> · SSL ON · username = full Zoho email · password = Zoho app-password (accounts.zoho.com → Security → App Passwords).</p>
          <p><b>Gmail</b>: <code>imap.gmail.com</code> · port <code>993</code> · SSL ON · username = full Gmail · password = an app-password (Google Account → Security → 2-Step Verification → App passwords).</p>
        </div>
      </details>
    </div>
  );
}
